import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { Box, Drawer, IconButton } from '@mui/material'
import { darken, styled, Theme } from '@mui/material/styles'
import React from 'react'

import CustomTooltip from './tooltip'

export type Props = React.PropsWithChildren<{
  id?: string
  open: boolean
  width?: number
  closeTooltip?: string
  openButtonTop?: string
  openTooltip?: string

  onClose: () => void
  onOpen: () => void
}>

const DEFAULT_WIDTH = 300
const COLLAPSED_WIDTH = 20

const RightDrawer: React.FC<Props> = ({
  id,
  children,
  closeTooltip = 'Close',
  open,
  openButtonTop = '84px',
  openTooltip = 'Open',
  width = DEFAULT_WIDTH,
  onClose,
  onOpen,
}) => (
  <StyledDrawer anchor="right" id={id} open={open} variant="permanent" width={width} collapsedWidth={COLLAPSED_WIDTH}>
    {open && (
      <>
        <Header className="right-drawer-header">
          <CustomTooltip title={closeTooltip}>
            <IconButton aria-label={closeTooltip} onClick={onClose}>
              <ChevronRightIcon />
            </IconButton>
          </CustomTooltip>
        </Header>
        {children}
      </>
    )}
    {!open && (
      <CustomTooltip title={openTooltip}>
        <HeaderOpenCont drawerOpen={open} width={width} collapsedWidth={COLLAPSED_WIDTH} top={openButtonTop}>
          <HeaderOpenBtn aria-label={openTooltip} onClick={onOpen}>
            <ChevronLeftIcon />
          </HeaderOpenBtn>
        </HeaderOpenCont>
      </CustomTooltip>
    )}
  </StyledDrawer>
)

export const createTransition = (props: string | string[], theme: Theme, open?: boolean) =>
  theme.transitions.create(props, {
    easing: theme.transitions.easing.sharp,
    duration: open ? theme.transitions.duration.enteringScreen : theme.transitions.duration.leavingScreen,
  })

const StyledDrawer = styled(Drawer, {
  shouldForwardProp: (prop) => !['width', 'collapsedWidth'].includes(String(prop)),
})<{ width: number; collapsedWidth: number }>(({ theme, open, width, collapsedWidth }) => ({
  flexShrink: 0,
  overflowX: 'hidden',
  width: open ? width : collapsedWidth,
  transition: createTransition('width', theme, open),
  '& .MuiDrawer-paper': {
    boxSizing: 'border-box',
    height: '100vh',
    paddingTop: '64px',
    width: open ? width : collapsedWidth,
    transition: createTransition('width', theme, open),
    display: 'flex',
    flexDirection: 'column',
  },
}))

const Header = styled(Box)(({ theme }) => ({
  alignItems: 'center',
  display: 'flex',
  height: '64px',
  justifyContent: 'flex-start',
  padding: theme.spacing(0, 1),
}))

const HeaderOpenBtn = styled(IconButton)(({ theme }) => ({
  height: 24,
  width: 24,
  border: `1px solid ${theme.palette.divider}`,
  background: theme.palette.background.paper,

  '&:hover': {
    background: darken(theme.palette.background.paper, 0.05),
  },
}))

const HeaderOpenCont = styled(Box, {
  shouldForwardProp: (prop) => !['drawerOpen', 'width', 'collapsedWidth', 'top'].includes(String(prop)),
})<{
  drawerOpen: boolean
  width: number
  collapsedWidth: number
  top: string
}>(({ theme, drawerOpen, width, collapsedWidth, top }) => ({
  position: 'fixed',
  top,
  backgroundColor: theme.palette.highlight.light,
  borderRadius: '50%',
  transform: 'translateX(50%)',
  opacity: drawerOpen ? 0 : 1,
  right: drawerOpen ? `${width}px` : `${collapsedWidth}px`,
  transition: createTransition(['all'], theme, drawerOpen),
}))

export default RightDrawer
